/**
 * UTM Link Builder — in-app tool to generate ad URLs with correct
 * utm_source / utm_campaign / utm_medium / utm_content / utm_term and
 * the optional `?store=<slug>` deep-link. Live preview + copy-to-clipboard
 * + QR code so growth can drop the URL straight into an ad manager.
 */
import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { Link2, Copy, CheckCircle2, RefreshCw, QrCode, Scissors, Trash2, BarChart3, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SOURCES = [
  { key: "google", utm: "google", medium: "cpc", label: "Google Ads" },
  { key: "meta", utm: "meta", medium: "paid_social", label: "Meta Ads" },
  { key: "instagram", utm: "instagram", medium: "paid_social", label: "Instagram" },
  { key: "tiktok", utm: "tiktok", medium: "paid_social", label: "TikTok Ads" },
  { key: "email", utm: "newsletter", medium: "email", label: "Newsletter" },
  { key: "partner", utm: "partner", medium: "referral", label: "Partner" },
];

const LANDING_OPTIONS = [
  { key: "/shoppers", label: "Shoppers landing page (recommended)" },
  { key: "/", label: "Marketing homepage" },
  { key: "/pricing", label: "Pricing page" },
];

const slug = (s) => (s || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

function buildUrl({ base, path, params }) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  const qs = q.toString();
  return `${base}${path}${qs ? `?${qs}` : ""}`;
}

export default function UtmBuilder() {
  const [source, setSource] = useState(SOURCES[0]);
  const [campaign, setCampaign] = useState("");
  const [content, setContent] = useState("");
  const [term, setTerm] = useState("");
  const [landing, setLanding] = useState(LANDING_OPTIONS[0].key);
  const [store, setStore] = useState("");
  const [stores, setStores] = useState([]);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(null);
  // Short-link state
  const [customSlug, setCustomSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState([]);
  const [shortHost, setShortHost] = useState("");
  const [analyticsFor, setAnalyticsFor] = useState(null);

  useEffect(() => {
    api.get("/config/short-domain")
      .then((r) => setShortHost(r.data?.host || ""))
      .catch(() => {});
  }, []);

  const loadLinks = async () => {
    try {
      const r = await api.get("/admin/short-links?limit=50");
      setLinks(r.data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadLinks(); }, []);

  useEffect(() => {
    // Pull LIVE storefronts (pages of 100, the API maximum) to power the deep-link dropdown. Best-effort.
    let live = true;
    (async () => {
      const all = [];
      for (let skip = 0; skip < 1000; skip += 100) {
        const r = await api.get("/v1/storefronts", { params: { limit: 100, skip } });
        const page = r.data.items || [];
        all.push(...page);
        if (page.length < 100) break;
      }
      if (live) setStores(all.filter((s) => s.status === "LIVE").map((s) => ({ slug: s.slug, name: s.name })));
    })().catch(() => live && setStores([]));
    return () => { live = false; };
  }, []);

  const base = typeof window !== "undefined"
    ? window.location.origin.replace(/\/$/, "")
    : "https://cyannect.com";
  const shortBase = shortHost ? `https://${shortHost}` : base;

  const url = useMemo(() => buildUrl({
    base,
    path: landing,
    params: {
      utm_source: source.utm,
      utm_medium: source.medium,
      utm_campaign: slug(campaign),
      utm_content: slug(content),
      utm_term: slug(term),
      store: store || undefined,
    },
  }), [base, landing, source, campaign, content, term, store]);

  useEffect(() => {
    QRCode.toDataURL(url, {
      margin: 1, width: 220,
      color: { dark: "#0e7490", light: "#ffffff" },
    }).then(setQr).catch(() => setQr(null));
  }, [url]);

  const canBuild = source && slug(campaign);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste into your ad manager.");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed — select the URL manually."); }
  };

  const reset = () => {
    setSource(SOURCES[0]); setCampaign(""); setContent(""); setTerm("");
    setLanding(LANDING_OPTIONS[0].key); setStore(""); setCustomSlug("");
  };

  const saveAsShortLink = async () => {
    if (!canBuild) return;
    setSaving(true);
    try {
      const body = { target_url: url };
      const clean = customSlug.trim().toLowerCase();
      if (clean) body.slug = clean;
      body.note = slug(campaign);
      const r = await api.post("/admin/short-links", body);
      const shortUrl = `${shortBase}/s/${r.data.slug}`;
      await navigator.clipboard.writeText(shortUrl).catch(() => {});
      toast.success(`Short link ${shortUrl} copied to clipboard.`);
      setCustomSlug("");
      loadLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create short link");
    } finally { setSaving(false); }
  };

  const deleteLink = async (slug_) => {
    if (!window.confirm(`Delete /s/${slug_}? Existing ads using it will 404.`)) return;
    try {
      await api.delete(`/admin/short-links/${encodeURIComponent(slug_)}`);
      toast.success("Short link deleted.");
      loadLinks();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="pf-card overflow-hidden" data-testid="utm-builder">
      <div className="p-4 border-b border-white/60 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-slate-900 text-lg inline-flex items-center gap-2">
            <Link2 className="w-4 h-4 text-cyan-600" /> UTM Link Builder
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Generate consistent ad URLs so attribution never breaks. Uses the same tags the analytics dashboard reads.</p>
          {shortHost && (
            <p className="text-[10px] text-emerald-700 mt-0.5 font-semibold" data-testid="short-domain-hint">
              Short domain active: <code className="font-mono">{shortHost}</code>
            </p>
          )}
        </div>
        <button onClick={reset} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                data-testid="utm-reset">
          <RefreshCw className="w-3 h-3" /> Reset
        </button>
      </div>
      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fields */}
        <div className="space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Source *</div>
            <div className="flex flex-wrap gap-2">
              {SOURCES.map((s) => (
                <button key={s.key} onClick={() => setSource(s)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition ${source.key === s.key ? "bg-cyan-500 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-cyan-300"}`}
                        data-testid={`utm-src-${s.key}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Campaign name *</div>
            <input value={campaign} onChange={(e) => setCampaign(e.target.value)}
                   placeholder="e.g. Fall Kickoff 2026"
                   className="input w-full" data-testid="utm-campaign" />
            {campaign && <div className="text-[10px] text-slate-500 mt-1 font-mono">utm_campaign = {slug(campaign)}</div>}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Content (optional)</div>
              <input value={content} onChange={(e) => setContent(e.target.value)}
                     placeholder="e.g. banner_a"
                     className="input w-full" data-testid="utm-content" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Term (optional)</div>
              <input value={term} onChange={(e) => setTerm(e.target.value)}
                     placeholder="e.g. running_shoes"
                     className="input w-full" data-testid="utm-term" />
            </label>
          </div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Landing page</div>
            <select value={landing} onChange={(e) => setLanding(e.target.value)}
                    className="input w-full" data-testid="utm-landing">
              {LANDING_OPTIONS.map((o) => <option key={o.key} value={o.key}>{`${o.label} — ${o.key}`}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Deep-link storefront (optional)</div>
            <select value={store} onChange={(e) => setStore(e.target.value)}
                    className="input w-full" data-testid="utm-store">
              <option value="">— None (generic landing) —</option>
              {stores.map((s) => (
                <option key={s.slug} value={s.slug}>{`${s.name} (${s.slug})`}</option>
              ))}
            </select>
            <div className="text-[10px] text-slate-500 mt-1 leading-relaxed">
              When set, the shopper's post-install deep-link opens <code className="font-mono">/store/{store || "slug"}</code> in the app.
            </div>
          </label>
        </div>

        {/* Preview + copy + QR */}
        <div className="space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Generated URL</div>
          <div className={`p-3 rounded-lg border ${canBuild ? "border-cyan-200 bg-cyan-50/40" : "border-slate-200 bg-slate-50"} text-xs font-mono break-all`}
               data-testid="utm-preview">
            {canBuild ? url : "Pick a source and campaign name to build a URL."}
          </div>
          <button onClick={copy} disabled={!canBuild}
                  className={`w-full px-3 py-2 rounded-full text-xs font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-60 ${copied ? "pf-btn-glass text-emerald-700" : "pf-btn-primary"}`}
                  data-testid="utm-copy">
            {copied ? <><CheckCircle2 className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy link</>}
          </button>

          {/* Save as short link */}
          <div className="pt-3 border-t border-slate-200 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1">
              <Scissors className="w-3 h-3" /> Shorten for character-capped ads
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500 font-mono whitespace-nowrap">/s/</span>
              <input value={customSlug} onChange={(e) => setCustomSlug(e.target.value)}
                     placeholder="auto"
                     className="input flex-1 text-xs"
                     data-testid="utm-short-slug" />
              <button onClick={saveAsShortLink} disabled={!canBuild || saving}
                      className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap disabled:opacity-60"
                      data-testid="utm-save-short">
                {saving ? "Saving…" : "Save & copy"}
              </button>
            </div>
            <div className="text-[10px] text-slate-500">Leave blank for a 6-character auto-slug. Slugs are lowercase-kebab, 3-32 chars.</div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold pt-2">QR preview</div>
          <div className="rounded-lg p-3 bg-white border border-slate-200 flex items-center justify-center"
               data-testid="utm-qr-wrap">
            {qr && canBuild ? (
              <img src={qr} alt="QR" className="w-40 h-40" data-testid="utm-qr" />
            ) : (
              <QrCode className="w-16 h-16 text-slate-300" />
            )}
          </div>
        </div>
      </div>

      {/* Existing short links */}
      {links.length > 0 && (
        <div className="p-5 border-t border-slate-200 bg-slate-50/60" data-testid="short-links-list">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3 inline-flex items-center gap-1">
            <Scissors className="w-3 h-3" /> Saved short links ({links.length})
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {links.map((l) => (
              <div key={l.slug} className="flex items-center gap-3 p-2 rounded bg-white border border-slate-200 text-xs"
                   data-testid={`short-link-${l.slug}`}>
                <div className="font-mono font-semibold text-slate-900 shrink-0">/s/{l.slug}</div>
                <div className="text-slate-500 truncate flex-1" title={l.target_url}>
                  {l.target_url}
                </div>
                <span className="pf-chip pf-chip-cyan text-[10px] shrink-0">{l.clicks || 0} click{l.clicks === 1 ? "" : "s"}</span>
                <button onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(`${shortBase}/s/${l.slug}`);
                            toast.success("Short URL copied.");
                          } catch { toast.error("Copy failed"); }
                        }}
                        className="text-slate-400 hover:text-cyan-600 p-1"
                        title="Copy short URL"
                        data-testid={`short-link-copy-${l.slug}`}>
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setAnalyticsFor(l)}
                        className="text-slate-400 hover:text-violet-600 p-1"
                        title="Click analytics"
                        data-testid={`short-link-analytics-${l.slug}`}>
                  <BarChart3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => deleteLink(l.slug)}
                        className="text-slate-400 hover:text-rose-600 p-1"
                        title="Delete"
                        data-testid={`short-link-delete-${l.slug}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics modal for a saved short link */}
      {analyticsFor && (
        <ShortLinkAnalyticsModal link={analyticsFor}
                                    shortBase={shortBase}
                                    onClose={() => setAnalyticsFor(null)} />
      )}
    </div>
  );
}

function ShortLinkAnalyticsModal({ link, shortBase, onClose }) {
  const [data, setData] = useState(null);
  const [window_, setWindow] = useState("30d");
  useEffect(() => {
    api.get(`/admin/short-links/${encodeURIComponent(link.slug)}/analytics?window=${window_}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [link.slug, window_]);

  const maxDay = Math.max(1, ...(data?.daily || []).map((d) => d.clicks));

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="short-link-analytics-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="pf-card p-6 max-w-2xl w-full space-y-4 relative">
        <button onClick={onClose}
                className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 p-1"
                data-testid="short-link-analytics-close">
          <X className="w-4 h-4" />
        </button>
        <div>
          <div className="pf-chip pf-chip-cyan w-fit"><BarChart3 className="w-3 h-3" /> Click analytics</div>
          <div className="font-display text-slate-900 text-xl mt-2 font-mono">{shortBase}/s/{link.slug}</div>
          <div className="text-xs text-slate-500 mt-1 truncate" title={link.target_url}>→ {link.target_url}</div>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-white/80 border border-slate-200 p-1 text-xs font-semibold w-fit">
          {["7d", "30d", "90d", "all"].map((w) => (
            <button key={w} onClick={() => setWindow(w)}
                    className={`px-3 py-1 rounded-full transition ${window_ === w ? "bg-cyan-500 text-white" : "text-slate-500 hover:text-slate-900"}`}
                    data-testid={`short-analytics-window-${w}`}>
              {w}
            </button>
          ))}
        </div>
        {!data && <div className="text-sm text-slate-500 p-4">Loading…</div>}
        {data?.error && <div className="text-sm text-rose-500 p-4">Could not load analytics.</div>}
        {data && !data.error && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="pf-card p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Clicks in window</div>
                <div className="font-display text-2xl text-slate-900 mt-1" data-testid="analytics-total">{data.total}</div>
              </div>
              <div className="pf-card p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Lifetime clicks</div>
                <div className="font-display text-2xl text-slate-900 mt-1">{data.lifetime_clicks}</div>
              </div>
            </div>
            {data.daily.length > 0 && (
              <div className="pf-card p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Daily clicks</div>
                <div className="flex items-end gap-1 h-24">
                  {data.daily.map((d) => (
                    <div key={d.day} className="flex flex-col items-center gap-0.5 min-w-[24px]" title={`${d.day}: ${d.clicks}`}>
                      <div className="w-2 rounded-t bg-cyan-500" style={{ height: `${(d.clicks / maxDay) * 80}px` }} />
                      <div className="text-[9px] text-slate-400 font-mono">{d.day.slice(5)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="pf-card p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Top referrers</div>
              {data.top_referrers.length === 0 && <div className="text-xs text-slate-500">No referrers recorded yet.</div>}
              <div className="space-y-1">
                {data.top_referrers.map((r) => (
                  <div key={r.referrer} className="flex items-center gap-2 text-xs">
                    <div className="flex-1 truncate font-mono text-slate-600" title={r.referrer}>{r.referrer}</div>
                    <div className="font-mono font-semibold text-slate-900">{r.clicks}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
